import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import moment from 'moment';

import { DATA_TABLE_SOURCE_PURCHASE_REQUESTS } from 'common/dataTableConstants';

import {
  createFormValidationFn,
  createInitialValues,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import log from 'client/lib/log';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import {
  getAccountingDashboardHeaders,
  makeAccountingDashboardRows,
} from 'client/components/UPAdminAccountingDashboard/accountingDashboardRows';

import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import { SR_TABLE_THEME_PARTNER, SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_BLUE_INVERTED,
  SR_BUTTON_HEIGHT_SHORT,
} from 'client/components/SrButton/SrButton';
import SrLink from 'client/components/SrLink/SrLink';

const getFormFields = ({ onSearchChange, onLeaseStartDateChange }) => {
  return [
    {
      id: 'searchTerm',
      type: 'short_text',
      properties: {
        placeholder: 'Search Dealer Portal, Provider, Renter',
        onChange: onSearchChange,
      },
    },
    {
      id: 'leaseStartDate',
      type: 'date_picker',
      properties: {
        placeholder: 'Move In Date',
        dateFormat: 'MM/DD/YYYY',
        readOnly: false,
        onChange: onLeaseStartDateChange,
      },
    },
    {
      id: 'tableTheme',
      type: 'select',
      properties: {
        choices: [
          {
            value: SR_TABLE_THEME_PARTNER_CLEAR,
            label: 'Expanded view',
          },
          {
            value: SR_TABLE_THEME_PARTNER,
            label: 'Compact view',
          },
        ],
      },
    },
  ];
};

const UPAdminAccountingDashboard = ({ utilityDealers, utilitySalesReps }) => {
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);
  const [updateModalProps, setUpdateModalProps] = useState(null);
  const [highlightRowId, setHighlightRowId] = useState(null);

  const dispatch = useDispatch();

  // Memoize so SrDataTableUI's `headers` prop is referentially stable across renders;
  // categorical filter options only need to recompute when the live lists change.
  const dashboardHeaders = useMemo(
    () => getAccountingDashboardHeaders({ utilityDealers, utilitySalesReps }),
    [utilityDealers, utilitySalesReps],
  );

  const closeModal = () => {
    setUpdateModalProps(null);
  };

  return (
    <div>
      <Formik
        initialValues={{
          searchTerm: '',
          tableTheme: SR_TABLE_THEME_PARTNER_CLEAR,
        }}
      >
        {(formikBag) => {
          return (
            <SrDataTableConnected
              dataSource={DATA_TABLE_SOURCE_PURCHASE_REQUESTS}
              initialViewSettings={{
                sort: {
                  accountRequestedAt: 'desc',
                },
              }}
              count={20}
              itemsPropName="rows"
              resetOnUnmount
            >
              {({
                rows,
                reloadItems,
                viewSettings,
                dataTableApiState,
                onInfiniteScrollWrapperRef,
              }) => {
                const formFields = getFormFields({
                  onSearchChange: (event) => {
                    const searchTerm = event.target?.value || '';
                    formikBag.setFieldValue('searchTerm', searchTerm);

                    viewSettings.update({
                      ...viewSettings,
                      fuzzy: !searchTerm
                        ? null
                        : {
                            queryString: searchTerm,
                            columns: ['providerName', 'dealerName', 'renterName', 'renterNumber'],
                          },
                    });
                  },
                  onLeaseStartDateChange: (date) => {
                    formikBag.setFieldValue('leaseStartDate', date);
                    viewSettings.update({
                      ...viewSettings,
                      filter: {
                        ...viewSettings.filter,
                        leaseStartDate: moment(date).format('YYYY-MM-DD'),
                      },
                    });
                  },
                });
                const getFormFieldsById = createFormFieldsByIdGetter(formFields);

                const updateActivationItem = ({ actionType, itemId, payload }) => {
                  setIsUpdatingItem(true);
                  dispatch(requestUtilityApi, {
                    action: actionType,
                    payload,
                    onSuccess() {
                      reloadItems({
                        count: 1,
                        filter: {
                          utilityActivationItemId: itemId,
                        },
                      }).then(() => {
                        setIsUpdatingItem(false);
                        closeModal();
                      });
                    },
                    onError(error) {
                      global.alert(error.stack || error.message);
                      log.error(error);

                      reloadItems({
                        count: 1,
                        filter: {
                          utilityActivationItemId: itemId,
                        },
                      }).then(() => {
                        setIsUpdatingItem(false);
                        closeModal();
                      });
                    },
                  });
                };

                const processedRows = makeAccountingDashboardRows({
                  rows,
                  updateActivationItem,
                  onEditableClick: setUpdateModalProps,
                  utilityDealers,
                  utilitySalesReps,
                  highlightRowId,
                  setHighlightRowId,
                });

                // Active filter count + reset, used by the "Reset filters [N]"
                // pill. Counts every key in viewSettings.filter — including
                // `leaseStartDate`, which is shared by the dashboard-level Move
                // In Date picker AND the per-column Move-In Date filter.
                // Prior behavior preserved leaseStartDate on reset so the
                // dashboard picker held its value, but that meant operators
                // couldn't clear a stale Move In Date in one click. Per QA
                // ask: every filter, including Move In Date, gets cleared.
                //
                // viewSettings is undefined on the initial render (before the
                // dataTableUIControls HOC initializes baobab state). Optional-
                // chain through it so SSR / first-paint doesn't 500 — once
                // settings exist on a subsequent render, the pill mounts.
                const activeFilterKeys = Object.keys(viewSettings?.filter || {});
                const hasFuzzySearch = !!(viewSettings?.fuzzy?.queryString);
                const activeColumnFilterCount =
                  activeFilterKeys.length + (hasFuzzySearch ? 1 : 0);
                const hasActiveColumnFilters = activeColumnFilterCount > 0;
                const resetColumnFilters = () => {
                  if (!viewSettings) return;
                  viewSettings.update({
                    ...viewSettings,
                    filter: {},
                    fuzzy: null,
                  });
                  // The dashboard-level Move In Date and Search inputs are
                  // formik-managed text fields; clearing the underlying view
                  // state alone leaves the input boxes still showing the old
                  // values. Also reset the formik fields so the inputs visually
                  // empty out and the user sees a clean slate.
                  formikBag.setFieldValue('leaseStartDate', '');
                  formikBag.setFieldValue('searchTerm', '');
                };

                return (
                  <div ref={onInfiniteScrollWrapperRef}>
                    <SrBox paddingBottom="20px">
                      <SrFlex gap="20px" align="baseline">
                        <SrText font="greycliff" weight="700" fontSize="28px" lineHeight="34px">
                          Accounting Dashboard
                        </SrText>
                        <SrBox width="280px">
                          <SrFormikField
                            formField={getFormFieldsById('searchTerm')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox width="180px">
                          <SrFormikField
                            formField={getFormFieldsById('leaseStartDate')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox width="180px">
                          <SrFormikField
                            formField={getFormFieldsById('tableTheme')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        {hasActiveColumnFilters && (
                          // Lives in the dashboard rather than SrDataTableUI because
                          // the dashboard is the only place that knows which filter
                          // keys are dashboard-level (leaseStartDate) vs column-level.
                          // SrButton + ADOBE_BLUE_INVERTED + HEIGHT_SHORT matches the
                          // surrounding adobe-themed form fields (44px tall, 16px
                          // font, blue-on-grey) so it reads as part of the same
                          // control row instead of as a tacked-on chip.
                          <SrButton
                            theme={SR_BUTTON_THEME_ADOBE_BLUE_INVERTED}
                            heightType={SR_BUTTON_HEIGHT_SHORT}
                            onClick={resetColumnFilters}
                            width="auto"
                            minWidth="auto"
                            padding="0 16px"
                          >
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '22px',
                                  height: '22px',
                                  padding: '0 7px',
                                  borderRadius: '11px',
                                  background: '#007aff',
                                  color: '#fff',
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                              >
                                {activeColumnFilterCount}
                              </span>
                              <span>Reset filters</span>
                            </span>
                          </SrButton>
                        )}
                        <SrBox marginLeft="auto">
                          <SrLink
                            to="/utility-profit/admin/dashboard/accounting-non-internet"
                            target="_blank"
                          >
                            <SrText
                              font="greycliff"
                              color="blue300"
                              cursor="pointer"
                              weight="800"
                              fontSize="16px"
                              lineHeight="50px"
                            >
                              Go to Non-Internet Accounting Dashboard
                            </SrText>
                          </SrLink>
                        </SrBox>
                      </SrFlex>
                    </SrBox>
                    <SrDataTableUI
                      theme={formikBag.values.tableTheme}
                      className="tableAccountDashboard"
                      dataTableViewSettings={viewSettings}
                      dataTableApiState={dataTableApiState}
                      headers={dashboardHeaders}
                      rows={processedRows}
                      sticky
                    />
                  </div>
                );
              }}
            </SrDataTableConnected>
          );
        }}
      </Formik>
      {updateModalProps && (
        <SrDarkModal>
          <SrModalContent onCloseIconClick={closeModal}>
            <Formik
              validate={(values) => {
                const formFields = updateModalProps.getFormFields(values);
                return createFormValidationFn(formFields)(values);
              }}
              initialValues={createInitialValues({
                formFields: updateModalProps.getFormFields(),
                defaultValues: updateModalProps.defaultValues,
              })}
              onSubmit={updateModalProps.onSubmit}
              render={(formikBag) => {
                return (
                  <Form>
                    {updateModalProps.getFormFields(formikBag.values).map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    ))}
                    <SrBox margin="20px 0">
                      <SrButton
                        type="submit"
                        theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                        disabled={isUpdatingItem}
                      >
                        Save
                      </SrButton>
                    </SrBox>
                    <SrButton
                      type="button"
                      theme={SR_BUTTON_THEME_ADOBE_GREY}
                      disabled={isUpdatingItem}
                      onClick={closeModal}
                    >
                      Cancel
                    </SrButton>
                  </Form>
                );
              }}
            />
          </SrModalContent>
        </SrDarkModal>
      )}
    </div>
  );
};

UPAdminAccountingDashboard.propTypes = {
  utilityDealers: PropTypes.array,
  utilitySalesReps: PropTypes.array,
};

UPAdminAccountingDashboard.defaultProps = {
  utilityDealers: [],
  utilitySalesReps: [],
};

export default UPAdminAccountingDashboard;
